<template>
  <div>
    <Heading v-if="title" level="h6" class="mb-3 !mt-0">{{ title }}</Heading>
    <div v-if="hasData" :style="{ height: `${height}px` }">
      <canvas ref="chartCanvas"></canvas>
    </div>
    <EmptyState 
      v-else
      icon="chart" 
      :message="emptyMessage" 
      :height="`h-[${height}px]`"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { Chart, LineController, LineElement, PointElement, LinearScale, CategoryScale, Title, Tooltip, Legend } from 'chart.js'
import Heading from '@/components/ui/Heading.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import { t } from '@/i18n/en-us'

Chart.register(LineController, LineElement, PointElement, LinearScale, CategoryScale, Title, Tooltip, Legend)

const props = defineProps({
  data: {
    type: Array,
    required: true
  },
  height: {
    type: Number,
    default: 300
  },
  dataKey: {
    type: String,
    default: 'value'
  },
  label: {
    type: String,
    default: 'Data'
  },
  title: {
    type: String,
    default: ''
  },
  emptyMessage: {
    type: String,
    default: () => t('reports.noDataAvailable')
  },
  color: {
    type: String,
    default: 'rgb(59, 130, 246)'
  }
})

const chartCanvas = ref(null)
let chartInstance = null

// Computed
const hasData = computed(() => props.data && props.data.length > 0)

const createChart = () => {
  if (!chartCanvas.value || !props.data || props.data.length === 0) return

  const ctx = chartCanvas.value.getContext('2d')
  
  // Destroy existing chart
  if (chartInstance) {
    chartInstance.destroy()
  }

  // Convert color to rgba for background
  const borderColor = props.color || 'rgb(59, 130, 246)'
  const backgroundColor = borderColor.replace('rgb', 'rgba').replace(')', ', 0.1)')
  
  chartInstance = new Chart(ctx, {
    type: 'line',
    data: {
      labels: props.data.map(d => d.date || d.label),
      datasets: [{
        label: props.label,
        data: props.data.map(d => d[props.dataKey]),
        borderColor: borderColor,
        backgroundColor: backgroundColor,
        tension: 0.4,
        fill: false
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          mode: 'index',
          intersect: false
        }
      },
      scales: {
        x: {
          grid: {
            display: false
          }
        },
        y: {
          beginAtZero: true,
          max: 100,
          ticks: {
            precision: 0,
            callback: function(value) {
              return value + '%'
            }
          }
        }
      }
    }
  })
}

onMounted(() => {
  createChart()
})

watch(() => props.data, async () => {
  // Wait for DOM to update (canvas to be re-mounted if it was removed)
  await nextTick()
  createChart()
}, { deep: true })
</script>

